import React from 'react';
import { motion } from 'framer-motion';
import {
    Mail,
    MapPin,
    Phone,
    Clock,
    Instagram,
    Facebook,
    Twitter,
    Youtube,
    MessageCircle
} from 'lucide-react';

const SupportInfoBit = ({ isMobile = false }) => {
    const supportInfo = [
        {
            icon: Mail,
            title: "Email Support",
            details: ["care@kromaic.com", "press@kromaic.com"],
            description: "24/7 support with 30-minute response time",
            color: "from-champagne/20 to-champagne/10"
        },
        {
            icon: Phone,
            title: "Phone Support",
            details: ["+1 (888) 888-8888"],
            description: "Mon-Fri, 9AM-6PM EST",
            color: "from-sage/20 to-sage/10"
        },
        {
            icon: MapPin,
            title: "Global Headquarters",
            details: ["123 Beauty Street", "New York, NY 10001", "United States"],
            description: "By appointment only",
            color: "from-nude/20 to-nude/10"
        },
        {
            icon: Clock,
            title: "Working Hours",
            details: ["Monday - Friday: 9AM-6PM EST", "Saturday: 10AM-4PM EST", "Sunday: Closed"],
            description: "Holiday hours may vary",
            color: "from-champagne/10 to-transparent"
        }
    ];

    const socialLinks = [
        { icon: Instagram, label: "@kromaicbeauty", color: "text-[#E4405F]" },
        { icon: Facebook, label: "/kromaic", color: "text-[#1877F2]" },
        { icon: Twitter, label: "@kromaic", color: "text-[#1DA1F2]" },
        { icon: Youtube, label: "KROMAIC TV", color: "text-[#FF0000]" }
    ];

    return (
        <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
        >
            {/* Support Cards */}
            <div className="space-y-4">
                {supportInfo.map((info, index) => (
                    <motion.div
                        key={info.title}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        whileHover={{ y: -4 }}
                        className={`p-4 md:p-5 rounded-2xl bg-gradient-to-r ${info.color} border border-white/50 backdrop-blur-sm`}
                    >
                        <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                                <info.icon className="w-5 h-5 text-champagne" />
                            </div>
                            <div className="flex-1">
                                <h3 className="font-medium text-charcoal mb-2">{info.title}</h3>
                                {info.details.map((detail, idx) => (
                                    <p key={idx} className="text-sm text-charcoal/70">{detail}</p>
                                ))}
                                <p className="text-xs text-charcoal/50 mt-2">{info.description}</p>
                            </div>
                        </div>
                    </motion.div>
                ))}
            </div>

            {/* Social Media */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="p-4 md:p-5 rounded-2xl bg-gradient-to-br from-champagne/5 to-sage/5"
            >
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-champagne/20 to-sage/20 flex items-center justify-center">
                        <MessageCircle className="w-5 h-5 text-champagne" />
                    </div>
                    <div>
                        <h3 className="font-medium text-charcoal">Connect With Us</h3>
                        <p className="text-sm text-charcoal/50">Follow for beauty rituals & tips</p>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                    {socialLinks.map((social, index) => (
                        <motion.a
                            key={social.label}
                            href="#"
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.4 + index * 0.1 }}
                            whileHover={{ scale: 1.05 }}
                            className="flex items-center gap-2 p-3 rounded-xl bg-white hover:bg-white/90 transition-colors"
                        >
                            <social.icon className={`w-4 h-4 ${social.color}`} />
                            <span className="text-sm text-charcoal/70 truncate">{social.label}</span>
                        </motion.a>
                    ))}
                </div>
            </motion.div>

            {/* Live Chat CTA */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="p-4 md:p-5 rounded-2xl bg-gradient-to-r from-champagne/10 to-sage/10"
            >
                <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center">
                        <MessageCircle className="w-4 h-4 text-champagne" />
                    </div>
                    <div>
                        <h4 className="font-medium text-charcoal">Live Chat Available</h4>
                        <p className="text-xs text-charcoal/50">Instant support during business hours</p>
                    </div>
                </div>
                <button className="w-full px-4 py-2 rounded-full bg-white border border-nude text-charcoal hover:border-champagne transition-colors text-sm">
                    Start Chat
                </button>
            </motion.div>
        </motion.div>
    );
};

export default SupportInfoBit;